import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  FileItem,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import Card from "./Card";
import ScrollModal from "@/components/common/scroll-modal";
import type { ExtensionRequestType } from "@/types/entities/extension";
import {
  getExternalDeadlineFromFormData,
  getRemainingTime,
} from "@/utils/procedures-ui";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onDenied: () => void;
  onSendToLead: () => void;
  data: ExtensionRequestType;
  canSendToLead?: boolean;
  canAction?: boolean;
}

const ExtensionMainModal = ({
  isOpen,
  onClose,
  onApprove,
  onDenied,
  onSendToLead,
  data,
  canAction,
  canSendToLead,
}: Props) => {
  const { showToast } = useToast();
  const handleOpenFile = (fileUrl: string) => {
    try {
      if (!fileUrl) {
        showToast({
          title: "Error",
          description: "La URL del archivo no está disponible",
          options: {
            type: "warning",
          },
        });
        return;
      }
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } catch {
      showToast({
        title: "Error",
        description: "Error al abrir el archivo",
        options: {
          type: "error",
        },
      });
    }
  };

  const executionData =
    data.node_time_extension.node_expiration.node.executions;
  const externalDeadline =
    getRemainingTime(getExternalDeadlineFromFormData(executionData), false) ||
    null;
  const leaderRequired = data.node_time_extension.leader_required;

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-[520px] h-[680px] max-w-[520px] max-h-[680px] rounded-xl"
      header={
        <div className="py-3 px-8">
          <div className="flex items-center justify-between w-full">
            <div className="text-center text-h2! font-semibold text-heading">
              <Heading variant="h5">Responder solicitud de plazo</Heading>
            </div>
            <Button
              variant="quiet"
              size="icon"
              aria-label="Cerrar"
              onClick={onClose}
              className="px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <>
          {canAction ? (
            <div
              className={`w-full grid grid-cols-1 ${!leaderRequired && "tablet:grid-cols-2"} py-3 px-8 gap-3`}
            >
              {!leaderRequired && (
                <div>
                  <Button
                    size="lg"
                    type="button"
                    variant="danger-tertiary"
                    onClick={onDenied}
                  >
                    Denegar
                  </Button>
                </div>
              )}
              <div className="flex gap-3 justify-end">
                {canSendToLead && (
                  <Button
                    size="lg"
                    type="button"
                    variant="tertiary"
                    onClick={onSendToLead}
                  >
                    Enviar al líder
                  </Button>
                )}
                {!leaderRequired && (
                  <Button size="lg" type="button" onClick={onApprove}>
                    Aprobar
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
        </>
      }
    >
      <div className="flex flex-col flex-1 min-h-0 px-4 sm:px-8 gap-4">
        <Card title="Tipo de aplazamiento">
          <div className="w-full flex items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <p className="font-normal leading-3xl text-body">Prórroga</p>
          </div>
        </Card>

        <Card title="Solicitante">
          <div className="w-full flex items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <Avatar size="sm" shape="circle" color="action">
              <AvatarFallback color="action">
                {data.applicant.first_name?.charAt(0).toUpperCase() ||
                  data.applicant.email?.charAt(0).toUpperCase() ||
                  "U"}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-md font-semibold leading-3xl text-body">
                {`${data.applicant.first_name || ""} ${data.applicant.last_name || ""}`.trim()}
              </p>
              <p className="text-sm font-normal leading-2xl text-body-quiet">
                {data.applicant.email}
              </p>
            </div>
          </div>
        </Card>

        <Card title="Nombre del paso">
          <div className="w-full flex justify-between items-center gap-3 px-3 py-2 bg-neutral-object rounded-lg">
            <div className="flex items-center gap-3">
              <Avatar size="2xs" shape="circle" color="action">
                <AvatarFallback color="action">
                  <Icon type="far" name="bars" size="xs" />
                </AvatarFallback>
              </Avatar>
              <span className="font-normal text-md leading-3xl text-body">
                {data?.formality_request?.flowExecution?.active_node_execution
                  ?.node.display_name || ""}
              </span>
            </div>
            <Badge
              size="sm"
              shape="square"
              variant="warning"
              fill="ghost"
              className="flex items-center"
            >
              {externalDeadline}
              <Icon
                type="fas"
                name="clock"
                size="xs"
                className="text-warning-quiet"
              />
            </Badge>
          </div>
        </Card>

        <Card title="Comentario">
          <p className="font-normal text-md leading-3xl text-body">
            {data.comments[0]?.text || "No hay comentarios"}
          </p>
        </Card>

        {data.files.length > 0 && (
          <Card title="Archivo de evidencia">
            <div className="flex flex-col gap-2">
              {data.files.map((file, index) => (
                <FileItem
                  key={file.name + index}
                  fileName={file.name}
                  actionAriaLabel={`Descargar archivo de evidencia ${index + 1}`}
                  onAction={() => {
                    handleOpenFile(file.url);
                  }}
                  tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  action={
                    <Icon
                      type="far"
                      name="arrow-up-right"
                      size="sm"
                      className="text-body-loud"
                    />
                  }
                />
              ))}
            </div>
          </Card>
        )}
      </div>
    </ScrollModal>
  );
};

export default ExtensionMainModal;
